import {AfterViewInit, ElementRef, Inject, Injectable, NgZone, PLATFORM_ID, Renderer2} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {debounceTime, Subject, Subscription, take} from 'rxjs';
import {BreakpointObserver, Breakpoints} from '@angular/cdk/layout';
import {takeUntil} from 'rxjs/operators';
import {isPlatformBrowser} from '@angular/common';

import {Globals} from '../../../core/services/globals';
import {ListingsService} from '../../../core/services/listings-service.interface';
import {LISTINGS_SERVICE_TOKEN} from "../../../core/services/listings-service.token";


import {SEOService} from '../../../core/services/seo.service';
import {TrackingService} from '../../../core/services/tracking.service';
import {DataLayerService} from '../../../core/services/data-layer.service';
import {Data, Listing} from "../../../core/models/listing";
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {ModalContentComponent} from "../modal-content/modal-content.component";
import {DomSanitizer, SafeHtml} from "@angular/platform-browser";
import {DOCUMENT} from "@angular/common";

@Injectable()
export abstract class BaseDashboardComponent implements AfterViewInit {
  // Shared properties
  listings: Listing[] = [];
  selectedListing: any | undefined;
  protected countNum = 0;
  protected isSelectedBln = false;
  protected listingsViewedNum = 0;
  protected isRegisteredBln = '0';
  protected loadingBln = false; // If needed for loading indicators
  protected currentScreenSize: string | undefined; // Responsive screen information
  protected destroyed$ = new Subject<void>(); // For cleanup
  public defaultMinPrice = '1000000'; // Shared default
  protected readonly HOSTNAME_TITLE_MAP = new Map<string, string>([
    ['oakvillesluxuryrealestate.com', 'Oakville Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['mississaugasluxuryrealestate.com', 'Mississauga Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['burlingtonluxuryrealestate.com', 'Burlington Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['southcaymanmarshall.com', 'Mississauga Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['portcreditluxuryrealestate.com', 'Port Credit Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['mineolaluxuryrealestate.com', 'Mineola Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['lorneparkluxuryrealestate.ca', 'Lorne Park Luxury Real Estate - Cayman Marshall International Realty Inc.'],
    ['caymanmarshall.com', 'Muskoka Luxury Real Estate - Cayman Marshall International Realty Inc. - Search'],
  ]);
  protected readonly HOSTNAME_DESCRIPTION_MAP = new Map<string, string>([
    ['oakvillesluxuryrealestate.com', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['mississaugasluxuryrealestate.com', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['burlingtonluxuryrealestate.com', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['southcaymanmarshall.com', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['portcreditluxuryrealestate.com', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['mineolaluxuryrealestate.com', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['lorneparkluxuryrealestate.ca', 'Search all Luxury Peel and Halton real estate listings, cottages, homes and land - Peel and Halton Luxury Listing Agent'],
    ['caymanmarshall.com', 'Muskoka Luxury Real Estate - Lake Rosseau Muskoka Joseph and Lake Of Bays'],
  ]);
  protected readonly HOSTNAME_KEYWORDS_MAP = new Map<string, string>([
    ['oakvillesluxuryrealestate.com', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['mississaugasluxuryrealestate.com', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['burlingtonluxuryrealestate.com', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['southcaymanmarshall.com', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['portcreditluxuryrealestate.com', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['mineolaluxuryrealestate.com', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['lorneparkluxuryrealestate.ca', 'Oakville luxury real estate, Mississauga luxury real estate,Peel and Halton luxury real estate, Peel and Halton luxury homes, Peel and Halton luxury single family homes, Peel and Halton luxury property, Peel and Halton property, Peel and Halton waterfront'],
    ['caymanmarshall.com', 'Muskoka luxury real estate, Muskoka luxury homes, Muskoka luxury single family homes, Muskoka luxury property, Muskoka property, Muskoka waterfront'],
  ]);

  protected readonly ROUTE_MAP = new Map<string, string>([
    ['/sold', 'Sold'],
    ['/search/sold', 'Sold'],
    ['/rentals', 'Rental'],
    ['/search/rentals', 'Rental'],
    ['/toronto', 'Toronto'],
    ['/search/toronto', 'Toronto'],
    ['/oakville', 'Oakville'],
    ['/search/oakville', 'Oakville'],
    ['/burlington', 'Burlington'],
    ['/search/burlington', 'Burlington'],
    ['/mississauga', 'Mississauga'],
    ['/search/mississauga', 'Mississauga'],
  ]);


  // Responsive display map for shared behavior
  protected displayNameMap = new Map([
    [Breakpoints.XSmall, 'XSmall'],
    [Breakpoints.Small, 'Small'],
    [Breakpoints.Medium, 'Medium'],
    [Breakpoints.Large, 'Large'],
    [Breakpoints.XLarge, 'XLarge'],
  ]);
  protected trackingURLStr = '/classes/REP_RETSTracking.php'; // URL to web api
  protected subscriptions: any[] = [];
  protected bsModalRef!: BsModalRef;
  private lastSetHeight = 0;
// Add this to track loading state
  protected isDetailLoading = false;
  protected screenSizeInitialized = false;
  protected platformId: object;


  constructor(
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected http: HttpClient,
    protected renderer: Renderer2,
    @Inject(LISTINGS_SERVICE_TOKEN) protected listingsService: ListingsService,
    protected SEOService: SEOService,
    public globals: Globals,
    protected elementRef: ElementRef,
    protected modalService: BsModalService,
    protected dataLayerService: DataLayerService,
    protected trackingService: TrackingService,
    protected breakpointObserver: BreakpointObserver,
    protected sanitizer: DomSanitizer,
    @Inject(DOCUMENT) protected document: Document,
    protected zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    this.platformId = platformId;
    console.log('Base Dashboard districtList: ' + this.globals.districtList);
    const title = this.getTitleForHostname(globals.hostnameStr);
    const description = this.getDescriptionForHostname(globals.hostnameStr);
    const keywords = this.getKeywordsForHostname(globals.hostnameStr);
    this.setSEOTags(title, description, keywords);
    this.trackAnalyticsPageView(title);
  }

  abstract ngAfterViewInit(): void;


  /**
   * Initialize dashboard base functionality - should be called in child components' ngOnInit
   */
  protected initializeBaseDashboard(): void {
    // SSR-only: increase initial listings batch to 100
    if (typeof window === 'undefined') {
      this.globals.startNum = 0;
      this.globals.endNum = 100;
      this.globals.page = 1;
    }

    // Apply navbar adjustments
    this.setupNavbar();

    // Detect screen changes
    this.setupBreakpointObserver();

    // Call shared initialization methods
    this.initializeSharedData();
    this.initializePageContent();
  }

  /**
   * Clean up base dashboard resources - should be called in child components' ngOnDestroy
   */
  protected cleanupBaseDashboard(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.removeStyle(this.document.body, 'padding-top');
      this.renderer.removeClass(this.document.body, 'dashboard-page');
      window.removeEventListener('resize', this.handleResize);
    }

    // Store current route as previous route before component is destroyed
    const currentRoute = this.router.url;
    // Only update if we're navigating away from details page
    if (currentRoute.includes('/fulldetails/')) {
      this.globals.previousRouteStr = currentRoute;
    }


    // Clean up subscriptions
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  /**
   * Initialize after view - should be called in child components' ngAfterViewInit
   */
  protected initializeAfterViewInit(): void {
    let header: HTMLElement | null = null;
    if (isPlatformBrowser(this.platformId)) {
      header = this.document.querySelector('.navbar-default');
      if (header != null) {
        header.classList.remove('navbar-shrink');
      }
      this.adjustNavbarPadding();
    }
  }

  /**
   * Setup the navbar with proper spacing and styling
   */
  protected setupNavbar(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.renderer.addClass(this.document.body, 'dashboard-page');
      this.adjustNavbarPadding();
      window.addEventListener('resize', this.handleResize);
    }
  }

  /**
   * Adjusts the body padding to account for the navbar height
   */
  protected adjustNavbarPadding(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        setTimeout(() => {
          this.setFixedNavbarPadding();
        }, 100);
        setTimeout(() => {
          this.setFixedNavbarPadding();
        }, 500);
        setTimeout(() => {
          this.setFixedNavbarPadding();
        }, 1000);
      });
    }
  }

  private setFixedNavbarPadding(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.document.querySelector('.navbar-default')) {
      return;
    }

    // Get current screen size and use responsive padding
    const screenSize = this.currentScreenSize || 'XLarge';

    // Define known heights for different screen sizes
    const navbarHeightMap: Record<string, number> = {
      'XSmall': 70,  // Mobile
      'Small': 80,   // Tablet
      'Medium': 85,  // Small desktop
      'Large': 89,   // Desktop
      'XLarge': 90   // Large desktop
    };

    // Get the appropriate height based on screen size
    const newHeight = navbarHeightMap[screenSize] || 90;
    if (Math.abs(this.lastSetHeight - newHeight) > 1) {
      this.renderer.setStyle(this.document.body, 'padding-top', `${newHeight}px`);
      this.lastSetHeight = newHeight;
    }

    return;/*
    // Apply the responsive padding
    this.zone.run(() => {
      this.renderer.setStyle(this.document.body, 'padding-top', `${navbarHeight}px`);
    });*/
  }


  /**
   * Handle window resize events to readjust padding
   */
  private handleResize = (): void => {
    this.adjustNavbarPadding();
  }


  // Common shared methods that can be used by both dashboards

  /**
   * Shared Breakpoint Observer Setup: Detect screen sizes and handle shared logic
   */
  protected setupBreakpointObserver(): void {
    this.breakpointObserver
      .observe([
        Breakpoints.XSmall,
        Breakpoints.Small,
        Breakpoints.Medium,
        Breakpoints.Large,
        Breakpoints.XLarge,
      ])
      .pipe(
        debounceTime(100),
        takeUntil(this.destroyed$)
      )
      .subscribe((result) => {
        for (const query of Object.keys(result.breakpoints)) {
          if (result.breakpoints[query]) {
            this.currentScreenSize =
              this.displayNameMap.get(query) ?? 'Unknown';
            // Mark that we have an initial screen size value
            this.screenSizeInitialized = true;

          }
        }
        console.log('Screen size updated:', this.currentScreenSize);
      });
  }


  /**
   * Shared Initialization: Perform common data fetching or state setup
   */
  protected initializeSharedData(): void {
    this.isRegisteredBln = this.globals.isRegisteredBln;
    this.defaultMinPrice = this.globals.searchMin || this.defaultMinPrice;
    this.listingsViewedNum = this.globals.listingsViewedNum;
  }


  protected trackAnalyticsEvent(obj: {
    _catStr: any;
    _eventStr: any;
    _labelStr: any;
  }) {
    console.log('trackAnalyticsEvent');
    console.log(obj._eventStr);
    // define properties
    const catStr = obj._catStr;
    const eventStr = obj._eventStr;
    const labelStr = obj._labelStr;
    let actionStr = 'Search Parameter Change';
    // fork, if this is a preview  then set the event action accordingly
    if (eventStr === 'preview') {
      actionStr = 'Preview Listing';
    }
    try {
      this.dataLayerService.logEvent(
        'custom.event.preview',
        catStr,
        actionStr,
        labelStr,
      );
      /*
      ga('send', {
        hitType: 'event',
        eventCategory: catStr,
        eventAction: actionStr,
        eventLabel: labelStr,
        'hitCallback' : function () {
          //alert("Event received");
        }
      });*/
    } catch (e) {
      console.log(e);
      console.log('ANALYTICS DISABLED');
    }
  }

  protected trackAnalyticsPageView(pageTitle: string): void {

    this.dataLayerService.pushEvent({
      event: 'pageview',
      pageUrl: this.router.url,
      pageTitle: pageTitle,
    });
  }

  /**
   * Fetches listings and handles the shared logic (tracking, error handling, etc.)
   */

  protected fetchListings(): void {
    this.loadingBln = true; // Set loading state
    this.listingsService.getListings().subscribe(
      (response: Data) => {
        const dataObj = response as Data;
        const listings = dataObj.data as Listing[];

        // Update shared state (Globals and component properties)
        this.globals.countNum = dataObj.meta.total;
        this.globals.page = dataObj.meta.current_page;
        this.globals.lastPage = dataObj.meta.last_page;
        this.listings = listings;
        this.countNum = this.globals.countNum;
        this.globals.listings = listings;
        this.loadingBln = false;

        // Shared tracking event
        this.trackEvent({_eventStr: 'search'});

        // Allow child components to handle post-fetch behavior
        this.onListingsFetched(listings);
      },
      (error: any) => {
        this.loadingBln = false;
        this.handleError(error); // Centralized error handling
      }
    );
  }

  protected fetchListing(listing: Listing) {
    this.globals.previousURLStr = this.router.url;
    this.isDetailLoading = true;

    // console.log('detail indirect');
    this.listingsService
      .getListing(listing.MLSNUM)
      .subscribe(
        (response: Data) => {
          const listingsDetails = response as Data;
          this.selectedListing = listingsDetails.data[0] as Listing;
          this.isDetailLoading = false;
          this.isSelectedBln = true;
          this.globals.selectedListing = listingsDetails.data[0];// Store current scroll position before navigating
          // Store current scroll position before navigating
          const isMobileView = ['XSmall', 'Small'].includes(this.currentScreenSize || '');
          this.globals.mobileBln = isMobileView;

          if (isMobileView) {
            console.log('storing mobile scroll position');
            // For mobile, store window scroll position
            this.globals.routeScrollPositions[this.router.url] = window.pageYOffset;
          } else {
            // For desktop, try to get resultsList scroll position
            if (isPlatformBrowser(this.platformId)) {
              const resultsElement = document.getElementById('resultsList');
              if (resultsElement) {
                this.globals.routeScrollPositions[this.router.url] = resultsElement.scrollTop;
              }
            }
          }


          // console.log('Selected Listing: ');
          // console.log(this.isSelectedBln);
          // track the event
          this.trackEvent({_eventStr: "preview"});
          this.trackingService.trackUserJourney(this.selectedListing.id, 'preview', 'Client Previewed Listing');
        },
        (error: any) => {
          this.isDetailLoading = false;
          this.handleError(error); // Centralized error handling
        },
      );
    if (!this.globals.initBln) {
      // add to the viewed counter
      this.globals.listingsViewedNum++;
      // console.log('viewed: ' + this.globals.listingsViewedNum);
      // console.log("viewed: " + this.listingsViewedNum);
      // fork, if the user has viewed 2 or more listings and has not regstered then show the registration modal
      if (this.globals.listingsViewedNum >= 2 && this.isRegisteredBln === '0') {
        this.showRegistration();
      }
    } else {
      this.globals.initBln = false;
    }
  }

  protected fetchMoreListings() {
    this.globals.loadingBln = true;
    this.listingsService.loadMore().subscribe(
      (response: any) => {
        // console.log(response.json());
        const dataObj = response as any;
        const dataArr = dataObj.data as Listing[];
        this.globals.countNum = dataObj.meta.total;
        this.listings = this.listings.concat(dataArr);
        this.globals.loadingBln = false;
        this.globals.listings = this.listings;
        // Allow child components to handle post-fetch behavior
        this.onListingsFetched(this.listings);
        // console.log(this.globals);
        // track the event
        this.trackEvent({_eventStr: 'view more listings'});
      },
      (error: any) => {
        this.handleError(error); // Centralized error handling
      }
    );
  }

  protected filterListings(searchObj: any) {
    // reset the paging
    this.resetPaging();
    // update the globe search properties
    //this.updateGlobalFilterSettings(searchObj);
    // upate the listings
    this.listingsService
      .getListings()
      .subscribe((response: Data) => {
        // console.log(response);

        const dataObj = response as Data;
        const listings = dataObj.data as Listing[];
        this.globals.countNum = dataObj.meta.total;
        this.listings = listings;
        this.countNum = this.globals.countNum;
        this.globals.listings = listings;
        this.globals.loadingBln = false;
        this.globals.listings = this.listings;
        // Allow child components to handle post-fetch behavior
        this.onListingsFetched(listings);
        // track the event
        this.trackEvent({_eventStr: 'search'});
      });
  }

  protected trackEvent(obj: { _eventStr: any }) {
    this.generateEventTrackingData(obj);
    return;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded;',
      'X-Requested-With': 'XMLHttpRequest',
    });
    const options = {
      headers: headers,
      params: this.generateEventTrackingData(obj),
    };
    const paramsObj = this.generateEventTrackingData(obj);
    const params = new HttpParams({
      fromObject: paramsObj as any,
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/x-www-form-urlencoded',
      }),
    };
    const response = this.http
      .post(this.trackingURLStr, params.toString(), httpOptions)
      .subscribe();
  }

  protected generateEventTrackingData(obj: { _eventStr: any }) {
    // console.log(this.globals);
    // define properties
    const emailStr = this.globals.emailStr;
    const userIDStr = this.globals.userIDStr;
    const ipAddrStr = this.globals.ipAddrStr;
    const sidStr = this.globals.sidStr;
    const idStr = this.globals.idStr;
    const eventStr = obj._eventStr;
    const eventFullStr =
      eventStr === 'preview'
        ? this.generatePreviewEventFullString()
        : this.generateEventFullString();
    // define properties
    const JSONObj = {
      idStr: idStr,
      emailStr: '',
      userIDStr: '',
      ipAddrStr: ipAddrStr,
      sessionIDStr: '',
      eventStr: eventStr,
      eventFullStr: eventFullStr,
      viewStr: 'list-view',
    };
    if (emailStr !== '') {
      JSONObj.emailStr = emailStr;
    } else {
      delete (JSONObj as any)['emailStr'];
    }
    if (userIDStr !== '') {
      JSONObj.userIDStr = userIDStr;
    } else {
      delete (JSONObj as any)['userIDStr'];
    }
    if (sidStr !== '') {
      JSONObj.sessionIDStr = sidStr;
    } else {
      delete (JSONObj as any)['sessionIDStr'];
    }
    // track the analtics event
    this.trackAnalyticsEvent({
      _catStr: 'RETS',
      _eventStr: eventStr,
      _labelStr: eventFullStr,
    });
    return JSONObj;
  }

  protected generateEventFullString() {
    // define properties
    const cityStr = this.globals.districtList;
    const stateStr = this.globals.stateStr;
    const propertyTypeStr = this.globals.propertyTypeStr;
    const bedroomsNum = this.globals.bedroomsNum;
    const bathroomsNum = this.globals.bathroomsNum;
    const squareFeetStr = this.globals.squareFeetStr;
    const searchMinNum = this.globals.searchMin;
    const searchMaxNum = this.globals.searchMax;
    const eventFullObj = {
      cityStr: cityStr,
      stateStr: stateStr,
      propertyTypeStr: propertyTypeStr,
      bedroomsNum: bedroomsNum,
      bathroomsNum: bathroomsNum,
      squareFeetStr: squareFeetStr,
      searchMin: searchMinNum,
      searchMax: searchMaxNum,
    };
    // return the json object as a string
    return JSON.stringify(eventFullObj);
  }

  protected generatePreviewEventFullString() {
    // define properties
    if (this.selectedListing === undefined) {
      return;
    }
    const MLSNum = this.selectedListing.MLSNUM;
    const propertyTypeStr = this.globals.propertyTypeStr;
    const eventFullObj = {
      MLSNum: MLSNum,
      propertyTypeStr: propertyTypeStr,
    };
    // return the json object as a string
    return JSON.stringify(eventFullObj);
  }

  private getTitleForHostname(hostname: string): string {
    return this.HOSTNAME_TITLE_MAP.get(hostname) ??
      'Cayman Marshall International Realty Inc.';
  }

  private getDescriptionForHostname(hostname: string): string {
    return this.HOSTNAME_DESCRIPTION_MAP.get(hostname) ??
      'Cayman Marshall International Realty Inc.';
  }

  private getKeywordsForHostname(hostname: string): string {
    return this.HOSTNAME_KEYWORDS_MAP.get(hostname) ??
      'PMuskoka luxury real estate, Muskoka luxury homes, Muskoka luxury single family homes, Muskoka luxury property, Muskoka property, Muskoka waterfront';
  }

  private setSEOTags(title: string, description: string, keywords: string): void {
    this.SEOService.setMetaTags({
      _titleStr: title,
      _descriptionStr: description,
      _urlStr: `https://www.${this.globals.hostnameStr}${this.router.url}`,
      _keywordsStr: keywords,
    });
  }

  protected initializePageContent(): void {
    const currentURL = this.router.url;
    this.setGlobalRoute(currentURL);
    this.adjustBodyOverflowForLargeScreens();

    if (this.shouldReloadListings()) {
      this.setGlobalDistrict();
      this.resetPaging();
    }
  }

  protected resetPreviousRoute(): void {
    if (this.globals && this.router) {
      this.globals.previousRouteStr = this.router.url; // Reset to the current route
    }
  }

  protected setGlobalRoute(url: string): void {
    const matchedRoute = [...this.ROUTE_MAP.keys()].find((route) => url.includes(route));
    this.globals.routeStr = matchedRoute ? this.ROUTE_MAP.get(matchedRoute)! : '';
  }

  protected setGlobalDistrict() {
    console.log('setGlobalDistrict');
    console.log(this.globals.routeStr);
    if (this.globals.isInitialLoad) return;
    if (this.globals.routeStr !== 'Sold' && this.globals.routeStr !== 'Rental') {
      if (this.globals.routeStr === '/search' || this.globals.routeStr === '/search/rentals') {
        this.globals.districtList = '';
        return;
      }
      this.globals.districtList = this.globals.routeStr;
    } else {
      this.globals.districtList = '';
    }
    console.log(this.globals.districtList);
  }

  protected handleDistrictDefaults(): void {
    //(this.globals.districtList);
    if (this.globals.districtList === 'ALL OF MUSKOKA') {
      this.globals.districtList = 'Mississauga';
    }

    if (this.globals.stateStr === 'ON') {
      this.globals.stateStr = 'Ontario';
    }
    if (this.globals.districtList === '') {
      this.setGlobalDistrict();
    }
  }

  protected setGlobalSearchMin(): void {
    this.globals.searchMin = this.defaultMinPrice;
  }


  protected adjustBodyOverflowForLargeScreens(): void {
    if (this.currentScreenSize === 'Large' || this.currentScreenSize === 'XLarge') {
      this.renderer.setStyle(document.body, 'overflow', 'hidden');
    }
  }

  protected shouldReloadListings(): boolean {
    //console.log('shouldReloadListings check');
    const currentRoute = this.router.url;
    console.log('Previous route:', this.globals.previousRouteStr);
    console.log('Current route:', currentRoute);
    console.log('Current listings count:', this.globals.listings.length);

    // No need to reload if we have listings and returning from details to same region
    if (this.globals.listings.length > 0 && this.globals.previousRouteStr.includes('/fulldetails/')) {
      console.log('Previous route includes details');
      const detailsRegion = this.getRegionFromRoute(this.globals.previousRouteStr);
      const currentRegion = this.getRegionFromRoute(currentRoute);
      if (detailsRegion === currentRegion) {
        return false;
      }
    }

    // Always reload if changing regions or if no listings
    const currentRegion = this.getRegionFromRoute(currentRoute);
    const previousRegion = this.getRegionFromRoute(this.globals.previousRouteStr);

    const shouldReload = (
      this.globals.listings.length === 0 ||
      currentRegion !== previousRegion ||
      (this.globals.previousRouteStr === '' && !this.globals.isInitialLoad)
    );

    console.log('Current region:', currentRegion);
    console.log('Previous region:', previousRegion);
    console.log('Should reload listings:', shouldReload);
    return shouldReload;
  }

  protected getRegionFromRoute(route: string): string {
    if (!route) return '';

    const parts = route.split('/');

    // Handle details pages first
    if (route.includes('/fulldetails/')) {
      // Get the region from the previous route instead
      return this.getRegionFromParts(parts);
    }

    return this.getRegionFromParts(parts);
  }

  protected getRegionFromParts(parts: string[]): string {
    // Special case for base muskoka routes
    if (parts[1] === 'search' && parts.length === 2) {
      return 'muskoka';
    }

    // Handle specific region routes
    if (parts.length >= 3 && parts[1] === 'search') {
      const region = parts[2].toLowerCase();
      switch (region) {
        case 'mississauga':
        case 'oakville':
        case 'burlington':
        case 'etobicoke':
        case 'toronto':
        case 'sold':
        case 'rentals':
          return region;
        default:
          return 'muskoka';
      }
    }

    // For direct region routes
    if (parts.length >= 2) {
      const region = parts[1].toLowerCase();
      switch (region) {
        case 'mississauga':
        case 'oakville':
        case 'burlington':
        case 'etobicoke':
        case 'toronto':
        case 'sold':
        case 'rentals':
        case 'search':
          return region;
        default:
          return 'muskoka';
      }
    }

    return 'muskoka'; // Default case
  }


  protected resetPaging() {
    this.globals.page = 1;
    this.globals.lastPage = 0;
    this.globals.limitNum = 10;
    this.globals.startNum = 0;
    this.globals.endNum = 10;
    this.globals.countNum = 0;
    this.globals.listingsViewedNum = 0;
    this.listings = [];
    this.globals.listings = [];
  }

  protected clearSubscriptions() {
    this.subscriptions.map((sub) => {
      sub.unsubscribe();
    });
    this.subscriptions = [];
  }

  protected showRegistration() {
    // console.log('showing registration');
    // console.log('viewed: ' + this.globals.listingsViewedNum);
    // define the config options for the modal
    const config = {
      animated: true,
      keyboard: false,
      backdrop: true,
      ignoreBackdropClick: true,
    };
    // this.modal.open(this.registrationModal);
    this.clearSubscriptions();
    this.subscriptions.push(
      this.modalService.onShown.subscribe((reason: string) => {
        // this.messages.push(`onShown event has been fired`);
        // console.log('shown');
      }),
    );
    this.subscriptions.push(
      this.modalService.onHide.subscribe((reason: string) => {
        // this.messages.push(`onShown event has been fired`);
        // console.log('closed');
        // console.log("Modal Content"+this.bsModalRef.content.isRegisteredBln);
        // set the user registration indicator to stop the registration page from showing up again

        if (this.bsModalRef !== undefined) {
          this.isRegisteredBln = this.bsModalRef.content.isRegisteredBln;
        }
        //
        // this.bsModalRef=null;
        // this.modalService.onShown=null;
        // this.registrationStepTwo();
      }),
    );
    this.bsModalRef = this.modalService.show(ModalContentComponent, config);
    this.bsModalRef.content.title = 'Registration';
  }

  protected async setScrollTo(nodeIdStr: string): Promise<void> {
    // Wait for screen size to be determined
    if (!this.currentScreenSize) {
      await new Promise<void>(resolve => {
        this.breakpointObserver
          .observe([
            Breakpoints.XSmall,
            Breakpoints.Small,
            Breakpoints.Medium,
            Breakpoints.Large,
            Breakpoints.XLarge,
          ])
          .pipe(take(1))
          .subscribe({
            next: (result) => {
              for (const query of Object.keys(result.breakpoints)) {
                if (result.breakpoints[query]) {
                  this.currentScreenSize = this.displayNameMap.get(query) ?? 'Unknown';
                }
              }
              resolve();
            }
          });
      });
    }

    const node = document.getElementById(nodeIdStr);
    const savedPosition = this.globals.routeScrollPositions[this.router.url];
    const isMobileView = ['XSmall', 'Small'].includes(this.currentScreenSize || '');
    this.globals.mobileBln = isMobileView;

    // Apply scroll position immediately
    if (!isMobileView && node && savedPosition !== undefined) {
      node.scrollTop = savedPosition;
    } else if (isMobileView && savedPosition !== undefined) {
      // For mobile view, use scrollTo with instant behavior
      window.scrollTo({
        top: savedPosition,
        behavior: 'instant' // Use 'instant' instead of 'smooth' for immediate scroll
      });
    }
  }


  protected gotoDetail(listing: Listing): void {
    //console.log('detail preview link clicked');
    if (this.globals.isRegisteredBln === '1') {
      this.isRegisteredBln = '1';
    }
    // console.log('Registration Status: ' + this.isRegisteredBln);
    // console.log('init indicator: ' + this.globals.initBln);
    // console.log('Router URL' + this.router.url);
    this.fetchListing(listing);
  }

  protected gotoDetailDirect(listing: Listing): void {
    //console.log('direct link clicked');
    //console.log("Current Screen Size" + this.currentScreenSize);
    if (this.globals.isRegisteredBln === '1') {
      this.isRegisteredBln = '1';
    }
    // console.log('Registration Status: ' + this.isRegisteredBln);
    // console.log('init indicator: ' + this.globals.initBln);
    // console.log('Router URL' + this.router.url);
    // console.log('Listing Data');
    // console.log(listing);
    // console.log('Listing Data End');
    // define properties
    const addressHyphenStr = (listing?.listingDetail?.address ?? listing.fullAddress).replaceAll(' ', '-');
    const MLSNum = listing.MLSNUM;
    const cityHyphenStr = listing.municipality.replaceAll(' ', '-');
    this.globals.previousURLStr = 'search/';

    // Store current scroll position before navigating
    const isMobileView = ['XSmall', 'Small'].includes(this.currentScreenSize || '');
    this.globals.mobileBln = isMobileView;

    if (isMobileView) {
      // For mobile, store window scroll position
      this.globals.routeScrollPositions[this.router.url] = window.pageYOffset;
    } else {
      // For desktop, try to get resultsList scroll position
      if (isPlatformBrowser(this.platformId)) {
        const resultsElement = document.getElementById('resultsList');
        if (resultsElement) {
          this.globals.routeScrollPositions[this.router.url] = resultsElement.scrollTop;
        }
      }
    }

    // console.log('Nav To: ' + 'search/fulldetails/' + addressHyphenStr + '/' + cityHyphenStr + '/' + MLSNum);
    if (!this.globals.initBln) {
      // add to the viewed counter
      this.globals.listingsViewedNum++;
      // console.log('viewed: ' + this.globals.listingsViewedNum);
      // console.log("viewed: " + this.listingsViewedNum);
      // fork, if the user has viewed 2 or more listings and has not regstered then show the registration modal
      if (this.globals.listingsViewedNum >= 2 && this.isRegisteredBln === '0') {
        this.showRegistration();
        return;
      }
    } else {
      this.globals.initBln = false;
    }
    // window.location.href=URLStr;
    // this.router.navigateByUrl('fulldetails/'+MLSNum);
    // Helper to clean the base route: strip query/hash and trailing slashes,
    // handle absolute URLs and SSR environment defensively.
    const cleanRoute = (route: string | undefined): string => {
      if (!route) return '/search';
      // If route contains protocol/host convert it to pathname via URL
      try {
        // On SSR window may be undefined; use location origin as fallback if available
        const base = (typeof window !== 'undefined' && window.location && window.location.origin)
          ? window.location.origin
          : 'http://localhost';
        const u = new URL(route, base);
        return u.pathname.replace(/\/+$/, ''); // remove trailing slashes
      } catch (err) {
        // Fallback for relative routes: strip query/hash and trailing slashes
        return route.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/search';
      }
    };

    // Determine correct base path for navigation by feature area
    // Prefer explicit known roots for Sold and Rentals to avoid malformed bases (e.g., '/Sold')
    const currentUrl = this.router.url || '';
    let basePath: string;
    if (currentUrl.includes('/search/sold') || this.globals.routeStr === 'Sold') {
      basePath = '/search/sold';
    } else if (currentUrl.includes('/search/rentals') || this.globals.routeStr === 'Rental') {
      basePath = '/search/rentals';
    } else if (currentUrl.startsWith('/search')) {
      basePath = '/search';
    } else {
      // Fallback to cleaned global route
      const rawBase = this.globals.routeStr || '';
      basePath = cleanRoute(rawBase);
      if (!basePath.startsWith('/')) {
        basePath = '/' + basePath;
      }
      if (basePath === '/') {
        basePath = '/search';
      }
    }

    // If address and city are identical, prefer the shorter details route that only uses MLS number
    const useShortDetails = addressHyphenStr && cityHyphenStr && addressHyphenStr.toLowerCase() === cityHyphenStr.toLowerCase();
    const targetUrl = useShortDetails
      ? `${basePath}/fulldetails/${MLSNum}`
      : `${basePath}/fulldetails/${addressHyphenStr}/${cityHyphenStr}/${MLSNum}`;

    console.log('Nav To: ' + targetUrl);

    // Navigate using the cleaned URL
    this.router.navigateByUrl(targetUrl);

  }

  protected sanitizedDescription(description: string): SafeHtml {
    // Truncate and sanitize the description safely.
    const truncatedDescription =
      description.length > 90
        ? description.slice(0, 90) + '...'
        : description;
    return this.sanitizeHtml(truncatedDescription);
  }

  protected sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  protected shouldShowProperty(): boolean {
    // Wait until breakpoint observer has emitted at least once
    if (!this.screenSizeInitialized) {
      return false;
    }

    const isMobileView = ['XSmall', 'Small'].includes(this.currentScreenSize || '');

    // On mobile we do not show the sidebar property panel
    if (isMobileView) {
      return false;
    }

    // Desktop: show the property panel only when a property is selected and not loading
    return !!this.isSelectedBln && !this.isDetailLoading;
  }


  protected shouldShowLoadingTemplate(): boolean {

    // Wait until breakpoint observer has emitted at least once
    if (!this.screenSizeInitialized) {
      return false;
    }
    const isMobileView = ['XSmall', 'Small'].includes(this.currentScreenSize || '');
    if (isMobileView) {
      return false;  // On mobile, don't show loading template in the sidebar
    }
    return this.isDetailLoading;  // On desktop, show loading when loading
  }


  protected get isMobileView(): boolean {
    return this.currentScreenSize ? !['Large', 'XLarge'].includes(this.currentScreenSize) : true;
  }


  protected get shouldShowPropertyDetails(): boolean {
    return this.isSelectedBln &&
      this.selectedListing !== undefined &&
      this.isMobileView;
  }


  protected onScrollDown() {
    // console.log("scrolled down!!");
    if (this.globals.loadingBln) {
      return;
    }
    if (this.listings.length >= this.globals.countNum) {
      return;
    }
    this.fetchMoreListings();
  }

  protected onScrollUp() {
    // console.log('scrolled up!!');
  }

  public onScrollUpGlobal() {
    // console.log('scrolled up!!');
  }

  protected onScrollDownGlobal() {
    // console.log("scrolled down global!!");
    if (this.globals.loadingBln) {
      return;
    }
    if (this.listings.length >= this.globals.countNum) {
      return;
    }
    this.fetchMoreListings();
  }

  protected handleError(error: any): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error.message || error);
  }

  /**
   * Abstract method: Child classes can handle listings after they are fetched
   * @param listings Fetched listings array
   */
  protected abstract onListingsFetched(listings: Listing[]): void;


  /**
   * Abstract method for child-specific initialization
   */
  protected abstract initDashboard(): void;
}
